import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import styles from './ModalHeader.module.css';

/**
 * Figma: `_Modal header` (2013:10605 → `Type=Left aligned`, 2013:10642). The
 * `_` marks it internal; `Modal` and `Drawer` both instance it, so it is shared
 * here rather than drawn twice.
 *
 * `Type` has a single option and does not become a prop. The two toggles do:
 *
 * - `Featured icon` (toggle) + the `placeholder` instance → `icon`. A slot, so
 *   absent means not rendered — Figma defaults it on with the placeholder
 *   glyph, which is a stand-in rather than a design.
 * - `X close button` (toggle) → `onClose`. The button is only rendered when
 *   there is something for it to do.
 */
export type ModalHeaderProps = {
  title: ReactNode;
  /** Figma `Supporting text`. */
  description?: ReactNode;
  /**
   * Not in Figma. Cuts the description to three lines behind a `See more`
   * toggle; expanded, it stops at ten lines (less on a short screen) and
   * scrolls. For a description that is content rather than copy — a post's or a
   * conversation's own text, which can run to thousands of characters. The
   * header does not scroll, so an unclamped one squeezes the panel's body to
   * nothing.
   *
   * Pass a string: new text then starts collapsed. A non-string description
   * keeps its expanded state across changes unless the caller keys the header.
   */
  clampDescription?: boolean;
  /** Figma `Featured icon` — a 12px glyph beside the title. */
  icon?: ReactNode;
  /** Figma `X close button`. Omit to render without one. */
  onClose?: () => void;
  /** Accessible name of the close button. Not in Figma. */
  closeLabel?: string;
  /** Ids the dialog points `aria-labelledby` / `aria-describedby` at. */
  titleId?: string;
  descriptionId?: string;
};

export function ModalHeader({
  title,
  description,
  clampDescription = false,
  icon,
  onClose,
  closeLabel = 'Close',
  titleId,
  descriptionId,
}: ModalHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.content}>
        <div className={styles.text}>
          <div className={styles.titleRow}>
            {icon !== undefined && <span className={styles.icon}>{icon}</span>}
            <Text
              as="h2"
              id={titleId}
              size="md"
              weight="regular"
              color="primary"
              className={styles.title}
            >
              {title}
            </Text>
          </div>
          {description !== undefined &&
            (clampDescription ? (
              /* Keyed by the text, so the next post opened in the same panel starts collapsed. */
              <ClampedDescription
                key={typeof description === 'string' ? description : undefined}
                descriptionId={descriptionId}
              >
                {description}
              </ClampedDescription>
            ) : (
              <Description id={descriptionId}>{description}</Description>
            ))}
        </div>
        {onClose !== undefined && (
          <Button
            hierarchy="ghostGrey"
            iconLeading={<Icon name="close" />}
            aria-label={closeLabel}
            onClick={onClose}
          />
        )}
      </div>
    </div>
  );
}

/** Figma `Supporting text`. */
function Description({
  id,
  className,
  children,
}: {
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Text
      as="p"
      id={id}
      size="sm"
      leading="normal"
      weight="regular"
      color="secondary"
      className={className ? `${styles.description} ${className}` : styles.description}
    >
      {children}
    </Text>
  );
}

/**
 * `clampDescription`. The toggle sits outside the described paragraph, so the
 * dialog's description is the text alone and never ends in "See more".
 */
function ClampedDescription({
  descriptionId,
  children,
}: {
  descriptionId?: string;
  children: ReactNode;
}) {
  const clipId = useId();
  const clipRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  /*
   * Collapsed, whether the clamp cut the text off: `See more` shows only then.
   * Expanded, whether the box scrolls: only then is it a focusable region. Two
   * values, so collapsing never reads an expanded measurement and unmounts the
   * focused toggle for a frame.
   *
   * Measured rather than guessed from the length, and again whenever the width
   * or the paragraph's height changes or a web font arrives, since each moves
   * the line breaks. A clamped paragraph keeps its height when Inter swaps in,
   * so only the font event catches that one.
   */
  const [cut, setCut] = useState(false);
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const clip = clipRef.current;
    const text = clip?.firstElementChild;
    if (!clip || !text) return;
    const measure = () => {
      if (expanded) setScrolls(clip.scrollHeight > clip.clientHeight);
      else setCut(text.scrollHeight > text.clientHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(clip);
    observer.observe(text);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [expanded]);

  const region = expanded && scrolls;
  return (
    <div className={styles.clamped}>
      <div
        id={clipId}
        ref={clipRef}
        className={expanded ? `${styles.clip} ${styles.expanded}` : styles.clip}
        role={region ? 'region' : undefined}
        aria-label={region ? 'Full text' : undefined}
        /*
         * A scroll box with nothing focusable inside has to take focus itself,
         * or keyboard users in browsers that don't focus scrollers (Safari)
         * cannot scroll past the tenth line.
         */
        tabIndex={region ? 0 : undefined}
      >
        <Description id={descriptionId} className={expanded ? undefined : styles.collapsed}>
          {children}
        </Description>
      </div>
      {(cut || expanded) && (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls={clipId}
          onClick={() => {
            /* The next expansion starts from the first line, not where this one was left. */
            if (clipRef.current) clipRef.current.scrollTop = 0;
            setExpanded(!expanded);
          }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}
